import { brand } from '../../theme/themePrimitives';
import DeleteMemberModal from './modal/delete_member';
import EditMemberModal from './modal/edit_member';
import ImageClass from '../../assets';
import { settingSelectedTeamMember } from '../../redux/slices/team';
import { TeamMemberObject } from '../../params/redux/team';
import { UserRole } from '../../params/redux';
import { useState } from 'react';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, IconButton, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Delete, Edit } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';

interface TeamTableProps {
    search: string;
    sort: boolean;
}

interface TeamMember {
    id: number;
    name: string;
    position: UserRole;
    email: string;
    phone: string;
}

const TeamTableComponent = ({ sort, search }: TeamTableProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const [editing, setEditing] = useState<number | null>(null);
    const [deleting, setDeleting] = useState<number | null>(null);
    const { teams, loaders } = useSelector((state: RootState) => state.team);
    // **Filter and Sort Logic**
    const filteredTeams = teams.filter((member: TeamMemberObject) => {
        const name = `${member.first_name || ''} ${member.last_name || ''}`.trim().toLowerCase();
        const email = member.email?.toLowerCase() || '';
        const phone = member.phone_number;
        return (
            !search ||
            name.includes(search.toLowerCase()) ||
            email.includes(search.toLowerCase()) ||
            phone.includes(search)
        );
    });

    const sortedMembers = sort
        ? [...filteredTeams].sort((a, b) => a.email.localeCompare(b.email))
        : filteredTeams;

    const rows = sortedMembers.map((teamMember: TeamMemberObject) => ({
        id: teamMember.id,
        name: `${teamMember.first_name} ${teamMember.last_name}`,
        position: teamMember.role,
        email: teamMember.email,
        phone: teamMember.phone_number
    })) || [];

    const handleDeleteSelection = (value: TeamMember) => {
        const selectedTeamMember = teams.find((item: TeamMemberObject) => item.id === value.id);
        dispatch(settingSelectedTeamMember(selectedTeamMember));
        setDeleting(value.id);
        // const selectedCustomer = customers.find((item: CustomerObject) => item.id === value.id);
        // setDeleteModal();
    };

    const editDealHandler = (value: TeamMember) => {
        const selectedTeamMember = teams.find((item: TeamMemberObject) => item.id === value.id);
        dispatch(settingSelectedTeamMember(selectedTeamMember));
        setEditing(value.id);
        // const selectedCustomer = customers.find((item: CustomerObject) => item.id === value.id);
        // dispatch(settingSelectedCustomer(selectedCustomer));
        // setEditModal();
    };

    const columns: GridColDef<TeamMember>[] = [
        {
            field: 'name',
            headerName: 'MEMBERS',
            flex: 1,
            renderCell: (params: GridRenderCellParams<TeamMember, string>) => <Box display="flex" gap={2} alignItems="center" my={1}>
                <Box display="flex" alignItems="center" justifyContent="center">
                    <img src={ImageClass.userPlacedholder} alt="User placehodler" height="30px" style={{
                        borderRadius: '50%'
                    }} />
                </Box>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">{params.value}</Typography>
            </Box>
        },
        {
            field: 'position',
            headerName: 'POSITION',
            flex: 1,
            renderCell: (params: GridRenderCellParams<TeamMember, string>) => <Box bgcolor={brand[100]} width="max-content" px={2} paddingY="5px" my={1} borderRadius={1}>
                <Typography variant="body1" color={brand[300]} fontWeight="bold">{params.row.position === 'organizationOwner'
                    ? 'ORGANIZATION OWNER'
                    : params.row.position === 'organizationAdmin'
                        ? 'ORGANIZATION ADMIN'
                        : params.row.position === 'salesMember'
                            ? 'SALES MEMBER'
                            : params.row.position === 'salesManager'
                                ? 'SALES MANAGER'
                                : params.row.position === 'companyManager'
                                    ? 'COMPANY MANAGER'
                                    : params.row.position === 'client'
                                        ? 'CUSTOMER'
                                        : 'USER'}</Typography>
            </Box>
        },
        { field: 'email', headerName: 'EMAIL', flex: 1 },
        { field: 'phone', headerName: 'PHONE NUMBER', flex: 1 },
        {
            field: 'id',
            headerName: 'ACTIONS',
            flex: 1,
            renderCell: (params: GridRenderCellParams<TeamMember, number>) => <Box display="flex" alignItems="center" gap="2px" my="5px">
                <IconButton onClick={() => editDealHandler(params.row)}>
                    <Edit fontSize="small" color="info" />
                </IconButton>
                <IconButton onClick={() => handleDeleteSelection(params.row)}>
                    <Delete fontSize="small" color="error" />
                </IconButton>
            </Box>
        }
    ];

    return <Box height="66vh" width="100%">
        <DataGrid
            rows={rows as TeamMember[]}
            loading={loaders.isLoading}
            columns={columns}
            pagination
            initialState={{
                pagination: {
                    paginationModel: {
                        pageSize: 20
                    }
                }
            }}
            sx={{
                '& .MuiDataGrid-row--borderBottom': {
                    backgroundColor: `${brand[50]} !important`
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold'
                },
                '& .MuiDataGrid-columnHeaders': {
                    fontWeight: 'bold'
                },
                '& .MuiDataGrid-cell': {
                    fontSize: '14px'
                },
                'width': '100%',
                'overflowX': 'hidden'
            }}
        />
        <EditMemberModal open={Boolean(editing)} onClose={() => setEditing(null)} />
        <DeleteMemberModal open={Boolean(deleting)} onClose={() => setDeleting(null)} />
    </Box>;
};

export default TeamTableComponent;